// Modelo do Dashboard. Tudo aqui é função pura sobre o que a API devolveu — a
// tela só desenha, e é aqui que os testes moram.
//
// Fuso: o back já entrega os buckets do resumo em hora LOCAL da operação
// ("2026-08-18T10:00", ver CallSummary.perHour). Comparar esse rótulo com o
// relógio do navegador só é seguro porque as duas pontas falam do mesmo fuso —
// por isso o corte de "hoje" e "ontem" é feito por comparação de STRING de dia,
// nunca convertendo o rótulo de volta para Date (que reintroduziria fuso).
import type { DialerAgentAvailability, LeadStatusDef } from "./api.js";

export interface HourBucket {
  hour: string;
  total: number;
  completed: number;
}

export interface PacePoint {
  hour: number;
  // null = hora que ainda não chegou. Zero seria mentira: diria "essa hora
  // aconteceu e ninguém ligou".
  today: number | null;
  yesterday: number | null;
}

export interface TodayPace {
  series: PacePoint[];
  today: number;
  // Quanto ontem tinha somado até ESTA hora — o único comparável honesto com um
  // dia ainda em curso.
  yesterdayToNow: number;
  // Variação percentual contra ontem até agora. null quando ontem foi zero:
  // "+∞%" não informa nada.
  deltaPct: number | null;
}

// "2026-08-18T10:00" → "2026-08-18" e 10.
function splitBucket(hour: string): { day: string; index: number } {
  return { day: hour.slice(0, 10), index: Number(hour.slice(11, 13)) };
}

function localDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function todayPace(buckets: HourBucket[], now: Date): TodayPace {
  const today = localDay(now);
  const yesterday = localDay(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  const currentHour = now.getHours();

  const todayByHour: number[] = Array.from({ length: 24 }, () => 0);
  const yesterdayByHour: number[] = Array.from({ length: 24 }, () => 0);
  for (const bucket of buckets) {
    const { day, index } = splitBucket(bucket.hour);
    if (Number.isNaN(index) || index < 0 || index > 23) continue;
    if (day === today) todayByHour[index]! += bucket.total;
    else if (day === yesterday) yesterdayByHour[index]! += bucket.total;
  }

  const series: PacePoint[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    today: hour <= currentHour ? todayByHour[hour]! : null,
    // Ontem é cortado na hora atual de propósito: o dia de ontem inteiro ao lado
    // de um dia pela metade faria a operação parecer sempre atrasada.
    yesterday: hour <= currentHour ? yesterdayByHour[hour]! : null,
  }));

  const sum = (values: number[]) => values.slice(0, currentHour + 1).reduce((acc, v) => acc + v, 0);
  const todayTotal = sum(todayByHour);
  const yesterdayToNow = sum(yesterdayByHour);

  return {
    series,
    today: todayTotal,
    yesterdayToNow,
    deltaPct:
      yesterdayToNow > 0
        ? Math.round(((todayTotal - yesterdayToNow) / yesterdayToNow) * 100)
        : null,
  };
}

export interface IdleAgent {
  userId: string;
  name: string;
  seconds: number;
}

// Quem está de plantão, aceitando lead e SEM ligação — o vendedor que o gestor
// precisa cutucar. Em ligação não conta (está trabalhando) e offline também não
// (nem entrou). Do mais parado para o menos.
export function idleAgents(agents: DialerAgentAvailability[], now: Date): IdleAgent[] {
  const nowMs = now.getTime();
  return agents
    .filter((a) => a.status === "AVAILABLE" && !a.inCall && a.availableSinceAt)
    .map((a) => ({
      userId: a.userId,
      name: a.name,
      seconds: Math.max(0, Math.round((nowMs - Date.parse(a.availableSinceAt!)) / 1000)),
    }))
    .filter((a) => Number.isFinite(a.seconds))
    .sort((a, b) => b.seconds - a.seconds);
}

export interface QueueColumn {
  key: string;
  label: string;
  color: string;
  total: number;
}

export interface QueueColumns {
  columns: QueueColumn[];
  // O que sobrou fora das colunas exibidas — some da lista, não da soma.
  rest: number;
  total: number;
}

// Contagem da fila por status, na ordem do PESO (é o peso que decide quem o
// discador entrega primeiro, não o sortOrder da tela). Status fora do catálogo
// ATIVO não entra: lead com status assim não está na fila.
export function queueColumns(
  totals: { status: string; total: number }[],
  statuses: LeadStatusDef[],
  limit: number,
): QueueColumns {
  const active = new Map(statuses.filter((s) => s.active).map((s) => [s.key, s]));
  const ranked = totals
    .flatMap(({ status, total }) => {
      const def = active.get(status);
      return def
        ? [{ key: def.key, label: def.label, color: def.color, total, weight: def.queueWeight }]
        : [];
    })
    .sort((a, b) => b.weight - a.weight || a.label.localeCompare(b.label));

  const columns = ranked.slice(0, limit).map(({ weight: _weight, ...col }) => col);
  return {
    columns,
    rest: ranked.slice(limit).reduce((acc, c) => acc + c.total, 0),
    total: ranked.reduce((acc, c) => acc + c.total, 0),
  };
}
